import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../../../components';
import {
  AnyPromotionInfo,
  Feature,
  findPromotionForProduct,
  getProductId,
  getTrueBundleAddOnsPayload,
  getTrueBundleComponents,
  getTrueBundleConfig,
  getTrueBundleSellUntilDate,
  isEcomFeature,
  isSamePlanIgnoringEcom,
  isTrueBundleFeature,
  PlanInterval,
  SubscriptionPlanPrice,
  SubscriptionSource,
  toEcomPlanCode,
  TrueBundleConfig,
} from '../../../../models';
import {
  fromOrder,
  fromPromotion,
  fromSubscription,
  useAppDispatch,
} from '../../../../store';
import {
  DEFAULT_CURRENCY_FORMAT_OPTIONS,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
} from '../../../../utils/Constants';
import {
  getCycleFromPathParam,
  getQuotaFromPathParam,
  sendTrackingData,
  TrackingPayload,
} from '../../../../utils/utils';
import {
  getDiscountPrice,
  getUnitPrice,
  useGetTrackingCookies,
  usePlanCartSnapshot,
} from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { useQuery } from '../../../../routes';
import {
  getTargetSelfServeSubscription,
  isPaidRecurlySubscription,
  isSelfServePaidSubscription,
  parseSubscriptionId,
} from '../../../../utils/subscriptionTarget';
import { SelectQuota } from './SelectQuota';
import {
  KSE_TESTING_PLAN_OPTION,
  ProductIconBadge,
  ProductIconKey,
  TRUE_TESTING_PLAN_OPTIONS,
} from './trueTestingProducts';
import { PlanBillingSwitch } from './PlanBillingSwitch';
import {
  getUniformCycleParam,
  KSE_STUDIO_PLAN_ROW,
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PLAN_OPTIONS,
  TRUE_BUNDLE_PURCHASE_FEATURES,
  TRUE_BUNDLE_STARTER_OPTIONS,
  TrueBundlePlan,
} from './trueBundlePlans';
import { getManualPurchaseProductCode } from './trueBundleProductCode';
import {
  gateCampaignForTrueBundleCart,
  getMatchedPreviewProduct,
  getTrueBundlePreviewRequests,
  hasPreviewResponseForRequests,
  hasTrueBundleInPreviewRequests,
  isBlockedByTrueBundleCart,
} from './trueBundlePreview';
import {
  getStarterBannerSavingPercent,
  getTrueBundleCardChips,
  getTrueBundleCardPrice,
  getTrueBundleSeatCount,
} from './trueBundleComponents';
import {
  StarterCardControls,
  StarterCardGrid,
  StarterCardSavingTooltip,
  StarterComponentList,
  StarterOfferBanner,
  StarterPlanCard,
} from './StarterPlanCards';
import { useStarterLayoutState } from './useStarterBundlePlans';
import {
  getStarterPackageOffer,
  getStarterPackagePrice,
  getStarterPackageUnitPrice,
  StarterPackageOffer,
} from './starterPackageCampaign';
import { getSummaryQuantityLabel, getSummaryUnitWord } from './OrderSummaryLineItems';

// One column template for BOTH tables on this page (the plan table and "More testing plans"),
// so Billing cycle / Quantity / Price line up across the two cards. Each fixed column is sized
// to the widest thing it must hold: billing switch 98px, quantity stepper 102px, and a price
// cell that carries either a saving badge next to the amount or the starter tier notes (138px).
// Trimming the two middle columns from 132px pushes them towards the right edge and hands the
// difference to the product name column, which used to wrap on the testing rows.
const TABLE_GRID_COLUMNS = 'minmax(0, 1fr) 104px 108px 160px';
const TABLE_GRID_COLUMN_GAP = 1.5;

const useStyles = makeStyles(theme => ({
  sectionLabel: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  planCard: {
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  selectedPlanCard: {
    borderColor: '#5959EB',
    boxShadow: '0 0 0 3px rgba(89, 89, 235, 0.08)',
  },
  planRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  planMain: {
    flex: 1,
    minWidth: 0,
  },
  planTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  planTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#22283C',
  },
  planPromoDescription: {
    color: '#6B7280',
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: theme.spacing(0.75),
  },
  planPromoHighlight: {
    color: '#16865F',
    fontWeight: 800,
  },
  planProducts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginLeft: theme.spacing(1),
  },
  productIconBadge: {
    display: 'inline-flex',
    cursor: 'help',
  },
  planPrice: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    minWidth: 136,
    textAlign: 'right',
    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-start',
      minWidth: 0,
      textAlign: 'left',
    },
  },
  billingCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-start',
    },
  },
  priceAmount: {
    color: '#22283C',
    fontWeight: 800,
    fontSize: 18,
  },
  priceHelp: {
    color: '#808B9A',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  includedFooter: {
    backgroundColor: '#FAFAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
  },
  includedTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  includedTitle: {
    color: '#808B9A',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  includedItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
  },
  includedCheckIcon: {
    color: '#12A877',
    fontSize: 18,
  },
  includedText: {
    color: '#6B7280',
    fontSize: 12,
    lineHeight: 1.5,
  },
  testingPlansSection: {
    marginTop: theme.spacing(3.5),
    // Alone on the page (the plan table is hidden once a bundle is owned).
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  testingPlanRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.625, 2),
    border: '1px solid #ececf4',
    borderRadius: 12,
    backgroundColor: '#fff',
    marginTop: theme.spacing(1.25),
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  testingPlanMain: {
    flex: 1,
    minWidth: 0,
  },
  testingPlanTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  testingPlanTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  testingPlanPrice: {
    fontSize: 13,
    fontWeight: 600,
    color: '#3c3a50',
    whiteSpace: 'nowrap',
    width: 72,
    flexShrink: 0,
    textAlign: 'right',
    [theme.breakpoints.down('tablet')]: {
      width: 'auto',
      textAlign: 'left',
    },
  },
  planInfoTooltip: {
    maxWidth: 300,
    padding: theme.spacing(1.75, 2),
    backgroundColor: '#16141d',
    color: '#d9d8e6',
    border: '1px solid #2c2940',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12.3,
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: 12,
    boxShadow: '0 16px 40px rgba(16,20,45,.32)',
  },
  planInfoTooltipTitle: {
    color: '#8d8ba8',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1.125),
  },
  tooltipList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    '& li': {
      position: 'relative',
      color: '#d9d8e6',
      fontSize: 12.5,
      lineHeight: 1.5,
      paddingLeft: theme.spacing(1.625),
      marginBottom: theme.spacing(1),
    },
    '& li::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 6,
      width: 5,
      height: 5,
      borderRadius: '50%',
      backgroundColor: '#19d89f',
    },
    '& li:last-child': {
      marginBottom: 0,
    },
  },
  productIconTooltip: {
    backgroundColor: '#16141d',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.625, 1.125),
    borderRadius: 7,
    maxWidth: 'none',
  },
  // ---- Table (mock "Confirm your plan") layout ----
  tableCard: {
    border: '1px solid #e6e4ef',
    borderRadius: 16,
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  tableHeaderRow: {
    display: 'grid',
    gridTemplateColumns: TABLE_GRID_COLUMNS,
    columnGap: theme.spacing(TABLE_GRID_COLUMN_GAP),
    alignItems: 'center',
    padding: theme.spacing(1.75, 3.25, 1.5),
    borderBottom: '1px solid #f1f0f7',
    [theme.breakpoints.down('tablet')]: {
      display: 'none',
    },
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: 500,
    color: '#6a6a6a',
  },
  tableHeaderCellEnd: {
    justifySelf: 'end',
  },
  // Billing cycle / Quantity: the label and the control below it are both centred in their
  // column, so the label stays centred over the control even when "+ Add" turns into the
  // wider quantity stepper.
  tableHeaderCellCenter: {
    justifySelf: 'center',
  },
  tableGridRow: {
    display: 'grid',
    gridTemplateColumns: TABLE_GRID_COLUMNS,
    columnGap: theme.spacing(TABLE_GRID_COLUMN_GAP),
    alignItems: 'center',
    padding: theme.spacing(3, 3.25),
    [theme.breakpoints.down('tablet')]: {
      gridTemplateColumns: '1fr',
      rowGap: theme.spacing(1.5),
    },
  },
  tableRowDivider: {
    height: 1,
    backgroundColor: '#f1f0f7',
    margin: theme.spacing(0, 3.25),
  },
  planNameDashed: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 18,
    fontWeight: 400,
    color: '#16141d',
    borderBottom: '1.5px dashed #c9c7d9',
    paddingBottom: 2,
    cursor: 'help',
  },
  cellStart: {
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('tablet')]: {
      justifySelf: 'start',
    },
  },
  cellSeats: {
    justifySelf: 'center',
    [theme.breakpoints.down('tablet')]: {
      justifySelf: 'start',
    },
  },
  addSeatButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: '#16141d',
    backgroundColor: '#fff',
    border: '1.5px solid #e6e4ef',
    borderRadius: 9,
    padding: theme.spacing(0.875, 1.75),
    fontSize: 12.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#f3f2fb',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: '#b3b1c4',
      backgroundColor: '#f7f7fb',
    },
  },
  // Keeps the hint tooltip alive on a disabled "Add" (a disabled button fires no mouse events).
  addSeatButtonHintAnchor: {
    display: 'inline-flex',
  },
  priceCell: {
    // Stretch across the grid column so the "Saving %" badge sits at the same
    // left edge on every row; the amount is pushed to the right edge instead.
    justifySelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    whiteSpace: 'nowrap',
  },
  priceCellStack: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  priceCellAmount: {
    fontSize: 18,
    fontWeight: 600,
    color: '#16141d',
    whiteSpace: 'nowrap',
  },
  starterTierNotes: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    fontSize: 12,
    lineHeight: 1.4,
    textAlign: 'right',
    color: '#5b6661',
  },
  starterBand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.75),
    margin: theme.spacing(-1.25, 3.25, 2.5),
    padding: theme.spacing(1.5, 2),
    border: '1px solid #ffeda2',
    borderRadius: 12,
    backgroundColor: '#fff9db',
    [theme.breakpoints.down('tablet')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      margin: theme.spacing(0, 2, 2),
    },
  },
  starterBandText: {
    minWidth: 0,
    fontSize: 13,
    lineHeight: 1.45,
    color: '#2c3a34',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('tablet')]: {
      whiteSpace: 'normal',
    },
  },
  starterBandTitle: {
    fontWeight: 600,
    color: '#8a7218',
  },
  starterClaimButton: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    height: 34,
    padding: theme.spacing(0, 2),
    border: 'none',
    borderRadius: 10,
    backgroundColor: '#0f8461',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
  },
  saveBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minHeight: 20,
    boxSizing: 'border-box',
    cursor: 'help',
    color: '#0c1411',
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: theme.spacing(0, 1.125),
    backgroundColor: '#dcf1e8',
  },
  saveInfoTooltip: {
    backgroundColor: '#16141d',
    color: '#d9d8e6',
    maxWidth: 250,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
    padding: theme.spacing(1.5, 1.75),
    border: '1px solid rgba(255, 255, 255, .14)',
    borderRadius: 12,
  },
  saveTooltipTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#19d89f',
    letterSpacing: '.01em',
    marginBottom: theme.spacing(0.75),
  },
  saveTooltipList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  saveTooltipItem: {
    display: 'flex',
    gap: theme.spacing(0.875),
    alignItems: 'flex-start',
    lineHeight: 1.4,
  },
  saveTooltipBullet: {
    flex: 'none',
    color: '#19d89f',
  },
  includedNote: {
    padding: theme.spacing(1.625, 3.25, 1.875),
    fontSize: 13,
    lineHeight: 1.4,
    color: '#73718a',
    backgroundColor: '#fafafc',
    borderRadius: theme.spacing(0, 0, 2, 2),
  },
  testingCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    border: '1px solid #eef2f0',
    borderRadius: 16,
  },
  testingTitle: {
    padding: theme.spacing(1.75, 3.25),
    fontSize: 14,
    fontWeight: 500,
    color: '#0f1a16',
  },
  testingHeaderRow: {
    display: 'grid',
    gridTemplateColumns: TABLE_GRID_COLUMNS,
    columnGap: theme.spacing(TABLE_GRID_COLUMN_GAP),
    alignItems: 'center',
    padding: theme.spacing(1.5, 3.25),
    borderTop: '1px solid #eef2f0',
    borderBottom: '1px solid #eef2f0',
    [theme.breakpoints.down('tablet')]: {
      display: 'none',
    },
  },
  testingGridRow: {
    display: 'grid',
    gridTemplateColumns: TABLE_GRID_COLUMNS,
    columnGap: theme.spacing(TABLE_GRID_COLUMN_GAP),
    alignItems: 'center',
    minHeight: 64,
    padding: theme.spacing(1.5, 3.25),
    [theme.breakpoints.down('tablet')]: {
      gridTemplateColumns: '1fr',
      rowGap: theme.spacing(1.5),
    },
  },
  testingInfoTooltip: {
    width: 280,
    maxWidth: 280,
    padding: theme.spacing(1.5, 1.75),
    backgroundColor: '#16141d',
    border: '1px solid #2c2940',
    borderRadius: 11,
    fontFamily: 'Inter, sans-serif',
    '& li': {
      color: '#ffffff',
      fontSize: 12,
      marginBottom: theme.spacing(0.75),
    },
  },
  testingNameDashed: {
    fontSize: 14,
    fontWeight: 400,
    color: '#16141d',
    borderBottom: '1.5px dashed #c9c7d9',
    paddingBottom: 2,
    cursor: 'help',
  },
  testingPriceCell: {
    justifySelf: 'end',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('tablet')]: {
      justifySelf: 'start',
      textAlign: 'left',
    },
  },
  testingPriceAmount: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  priceUnit: {
    fontSize: 12,
    color: '#5b6661',
    marginTop: 2,
  },
  // ---- Katalon Studio Starter card ("Choose your plan") ----
  studioControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    margin: theme.spacing(0, -1.75),
    padding: theme.spacing(1.25, 1.75, 0),
    borderTop: '1px solid #cfe6d8',
  },
  studioCycle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 13,
    color: '#16141d',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  studioSeats: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flex: 'none',
  },
  studioNotes: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(-0.5),
    fontSize: 12,
    lineHeight: 1.5,
    color: '#5b6661',
  },
}));

const MAX_SEATS = process.env.REACT_APP_TRUE_TEAM_MAX_SEATS;
const TRUE_SUBSCRIPTION_FEATURES = [
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
];

const getPlanProductIconKey = (labelId: string): ProductIconKey => {
  if (labelId.includes('studio')) {
    return 'studio';
  }

  if (labelId.includes('cloud')) {
    return 'cloud';
  }

  return 'testmgmt';
};

type FeatureIntervals = Partial<Record<Feature, PlanInterval>>;

export const TrueBundlePlanSelect = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { flags, ecomEligibility } = useLaunchDarkly();
  // Per-feature eCom channel eligibility (KSE product#1447 + New Packaging Sep 2026 R1–R3).
  // Empty until the BE has answered: every ecom feature then rests on its listed price.
  const ecomEligibilityByFeature = ecomEligibility ?? {};
  // TestOps is sold online only once its LD price key is seeded on the env (plan Decision 8:
  // the price key is the per-env rollout switch, the BE always answers every eligibility key).
  const testOpsRowEnabled = flags?.subscriptionPlanPrice?.testOpsG3Annually !== undefined
    || flags?.subscriptionPlanPrice?.testOpsG3Monthly !== undefined;
  const isTestingFeatureEnabled = (feature: Feature): boolean => (
    feature !== Feature.TESTOPS_G3 || testOpsRowEnabled
  );
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const studioSeatsParam = getQuotaFromPathParam(Feature.PER_USER_KSE);
  const runtimeSeatsParam = getQuotaFromPathParam(Feature.UNLIMITED_ENGINE);
  const crossBrowserSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_CROSS_BROWSER);
  const mobileAppSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_MOBILE_APP);
  const liveTestingSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_LIVE_TESTING);
  const testOpsSeatsParam = getQuotaFromPathParam(Feature.TESTOPS_G3);
  const manualBundleSeatsParam = getQuotaFromPathParam(Feature.TRUE_BUNDLE_MANUAL);
  const automationBundleSeatsParam = getQuotaFromPathParam(Feature.TRUE_BUNDLE_AUTOMATION);
  const bundleQuotaParamByFeature: Partial<Record<Feature, number>> = {
    [Feature.TRUE_BUNDLE_MANUAL]: manualBundleSeatsParam,
    [Feature.TRUE_BUNDLE_AUTOMATION]: automationBundleSeatsParam,
  };
  // getCycleFromPathParam relies on useQuery internally, so it must be called at the
  // top level of the component (never inside effects/loops). Resolve every SKU's cycle
  // param once here and reuse the values below.
  const studioCycleParam = getCycleFromPathParam(Feature.PER_USER_KSE);
  const runtimeCycleParam = getCycleFromPathParam(Feature.UNLIMITED_ENGINE);
  const crossBrowserCycleParam = getCycleFromPathParam(Feature.TESTCLOUD_SESSION_CROSS_BROWSER);
  const mobileAppCycleParam = getCycleFromPathParam(Feature.TESTCLOUD_SESSION_MOBILE_APP);
  const liveTestingCycleParam = getCycleFromPathParam(Feature.TESTCLOUD_SESSION_LIVE_TESTING);
  const testOpsCycleParam = getCycleFromPathParam(Feature.TESTOPS_G3);
  const cycleParamByFeature = useMemo<FeatureIntervals>(() => ({
    [Feature.PER_USER_KSE]: studioCycleParam,
    [Feature.UNLIMITED_ENGINE]: runtimeCycleParam,
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]: crossBrowserCycleParam,
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]: mobileAppCycleParam,
    [Feature.TESTCLOUD_SESSION_LIVE_TESTING]: liveTestingCycleParam,
    [Feature.TESTOPS_G3]: testOpsCycleParam,
  }), [
    studioCycleParam,
    runtimeCycleParam,
    crossBrowserCycleParam,
    mobileAppCycleParam,
    liveTestingCycleParam,
    testOpsCycleParam,
  ]);
  const targetSubscriptionId = parseSubscriptionId(get(PLAN_SUBSCRIPTION_ID_QUERY_PARAM));
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);
  // Every campaign the account is eligible for: each Starter is priced by ITS OWN campaign
  // (B.8). `undefined` = still loading, `[]` = none active.
  const activeCampaigns = useSelector(fromPromotion.selectActiveCampaigns);
  const orderPreviewRequest = useSelector(fromOrder.selectOrderPreviewRequest);
  const fallbackPreviewRequest = useSelector(fromOrder.selectTruePlatformPreviewRequest);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  // How many of the row's selected seats the preview's promotion actually covers.
  // Undefined while the preview for the current (productCode, quantity) pair hasn't
  // returned yet. The per-seat discount amount comes from the campaign, not here, so
  // the price stays free of the preview's co-term proration.
  const getAppliedPromoDiscountQuantity = (
    productCode: string,
    seats: number,
  ): number | undefined => {
    const request = getTrueBundlePreviewRequests(orderPreviewRequest, fallbackPreviewRequest)
      .find(req => req.productCode === productCode && req.quantity === seats);
    if (!request) {
      return undefined;
    }

    const matchedProduct = getMatchedPreviewProduct(request, orderPreviewResponse);
    if (!matchedProduct) {
      return undefined;
    }

    return matchedProduct.appliedPromotion?.discountQuantity
      ?? matchedProduct.discountQuantity;
  };
  // 1 order = 1 campaign: once a Starter bundle is in the cart (i.e. in the request the preview
  // and checkout send), the other rows are sold at their regular price - no KS Starter band, tag
  // or promo rate - and the bundle keeps its own campaign.
  const isTrueBundleInCart = hasTrueBundleInPreviewRequests(
    getTrueBundlePreviewRequests(orderPreviewRequest, fallbackPreviewRequest),
  );
  // The campaign that prices a given product. Several SINGLE_PRODUCT campaigns run in parallel
  // (one per Starter SKU), so a row must never borrow another product's campaign; while
  // `active-campaigns` is still in flight this falls back to the single `/active` campaign, which
  // keeps a one-campaign environment (KS Starter today) behaving exactly as before.
  // `bundleInCart` is overridden only by the Katalon Studio Starter card, which prices the choice
  // that REPLACES the bundle (picking it drops the bundle), see `getStudioCardData`.
  const getCampaignForProduct = (
    productCode: string,
    bundleInCart: boolean = isTrueBundleInCart,
  ): AnyPromotionInfo | null => (
    gateCampaignForTrueBundleCart(
      findPromotionForProduct(activeCampaigns, activePromotion, productCode),
      productCode,
      bundleInCart,
    )
  );
  // Discounted-seat tier of that campaign (e.g. "first 3 seats"), used for promo prices before
  // any quantity is selected and for the "Saving %" tooltip breakdown.
  const getCampaignTier = (
    productCode: string,
    matchExactCode = false,
  ): { seatCount: number; perUnitDiscount: number } | undefined => {
    const campaign = getCampaignForProduct(productCode);
    if (!campaign || !('discountedProducts' in campaign)) {
      return undefined;
    }

    // Default match is ecom-insensitive, mirroring how the preview response is joined
    // back to the request (isSamePlanIgnoringEcom). Callers that know the exact plan
    // code the BE will charge pass matchExactCode to mirror the BE's exact campaign
    // matching (SingleProductPromotionHandler) instead.
    const discounted = campaign.discountedProducts.find(
      product => (matchExactCode
        ? product.productCode === productCode
        : isSamePlanIgnoringEcom(product.productCode, productCode)),
    );
    if (!discounted || discounted.discountQuantity <= 0) {
      return undefined;
    }

    return {
      seatCount: discounted.discountQuantity,
      perUnitDiscount: discounted.discountAmount / discounted.discountQuantity,
    };
  };
  // The exact plan code the BE will charge for an eCom feature (KSE, KRE, TestCloud, TestOps G3
  // — ECOM_FEATURES): the BE routes ONLY an eligible account's legacy request to the ecom plan
  // code, then matches the campaign by exact product code (SingleProductPromotionHandler). A
  // legacy holder is not routed, so its legacy code is what gets matched at charge time.
  const getChargedEcomPlanCode = (feature: Feature, productCode: string): string => (
    ecomEligibilityByFeature[feature] ? toEcomPlanCode(productCode) : productCode
  );
  // Campaign tier mirroring the BE's exact matching for eCom features: show the promo price
  // only when the campaign discounts the exact code the preview will charge (ecom code when
  // eligible, legacy code otherwise) — otherwise the row would rest on a price the checkout
  // can never honor. TA/TP and other features keep the ecom-insensitive default match.
  const getGatedCampaignTier = (
    feature: Feature,
    productCode: string,
  ): { seatCount: number; perUnitDiscount: number } | undefined => {
    if (isEcomFeature(feature)) {
      return getCampaignTier(getChargedEcomPlanCode(feature, productCode), true);
    }
    return getCampaignTier(productCode);
  };
  // Starter-package offer, same exact-code mirror as getGatedCampaignTier.
  const getGatedStarterPackageOffer = (
    feature: Feature,
    productCode: string,
    bundleInCart: boolean = isTrueBundleInCart,
  ): StarterPackageOffer | undefined => {
    if (isEcomFeature(feature)) {
      const chargedCode = getChargedEcomPlanCode(feature, productCode);
      return getStarterPackageOffer(
        getCampaignForProduct(chargedCode, bundleInCart),
        chargedCode,
        true,
      );
    }
    return getStarterPackageOffer(getCampaignForProduct(productCode, bundleInCart), productCode);
  };
  // Seats still eligible for the campaign discount. maxDiscountLicense is a single pool
  // SHARED across every product in the campaign (e.g. TA + KSE draw from the same 3), so
  // the remaining quota is the campaign max minus what the current preview already applied
  // across all rows. Undefined when there is no single-product campaign (no pool to gate on).
  const getRemainingSharedQuota = (productCode: string): number | undefined => {
    const promotion = getCampaignForProduct(productCode);
    if (!promotion || !('maxDiscountLicense' in promotion)) {
      return undefined;
    }
    // Pooled PER CAMPAIGN: with one active campaign this is the previous behaviour (every applied
    // promotion carries that campaign code), with several it stops one Starter's cart from
    // draining another campaign's quota.
    const appliedQuantity = (orderPreviewResponse?.productsPreview ?? [])
      .filter(product => product.appliedPromotion?.campaignCode === promotion.campaignCode)
      .reduce((sum, product) => sum + (product.appliedPromotion?.discountQuantity ?? 0), 0);
    return Math.max(promotion.maxDiscountLicense - appliedQuantity, 0);
  };
  const lastRowPriceRef = useRef<Record<string, number>>({});
  // Unit price shown on a plan/product row. Mirrors the promotion the preview
  // actually applied: while every selected unit is covered by the promo quota,
  // show the per-unit promo price derived from the preview; once the selection
  // exceeds the quota (or no promo applies), show the standard unit price.
  // While the preview for the current quantity is still in flight, keep the
  // last shown price instead of flashing the base price until the response
  // lands. With no selection, fall back to the LD discount price so the row
  // matches the promo banner.
  const resolveRowUnitPrice = (
    feature: Feature,
    interval: PlanInterval,
    quantity: number,
    productCode: string,
  ): number => {
    const unitPrice = getUnitPrice(flags?.subscriptionPlanPrice, feature, interval, 'product');
    const discountPrice = getDiscountPrice(flags?.subscriptionPlanPrice, feature, interval);
    const hasDiscount = discountPrice > 0 && discountPrice < unitPrice;
    const fallbackPrice = hasDiscount ? discountPrice : unitPrice;
    const priceKey = `${feature}:${interval}`;

    const starterOffer = getGatedStarterPackageOffer(feature, productCode);
    // Tiered starter package: a per-seat discount capped at the campaign quota. The
    // row always advertises the discounted "first N" rate — the tier notes spell out
    // the standard price from seat N+1, so the price never flips with the quantity.
    if (starterOffer?.kind === 'tiered') {
      const tierUnitPrice = unitPrice - starterOffer.perUnitDiscount;
      const tieredRowPrice = tierUnitPrice > 0 && tierUnitPrice < unitPrice
        ? tierUnitPrice
        : unitPrice;
      lastRowPriceRef.current[priceKey] = tieredRowPrice;
      return tieredRowPrice;
    }

    // One-time starter package: an all-or-nothing bundle, not a per-seat rate. Below
    // the package size the row rests on the standard price (the claim-offer band sells
    // the deal); once claimed, every package seat shows the blended package rate.
    if (starterOffer) {
      const blendedUnitPrice = getStarterPackageUnitPrice(starterOffer, unitPrice);
      const starterUnitPrice = quantity >= starterOffer.seatCount
        && blendedUnitPrice > 0
        && blendedUnitPrice < unitPrice
        ? blendedUnitPrice
        : unitPrice;
      lastRowPriceRef.current[priceKey] = starterUnitPrice;
      return starterUnitPrice;
    }

    if (quantity <= 0) {
      const campaignPerUnitDiscount = getGatedCampaignTier(feature, productCode)
        ?.perUnitDiscount ?? 0;
      // The resting price previews the next seat's cost, so it only shows the promo rate
      // while the SHARED pool still has room. Once other rows have consumed the campaign
      // quota, the next seat here would bill at the standard price — show that instead.
      const remainingSharedQuota = getRemainingSharedQuota(productCode);
      const sharedPoolHasRoom = remainingSharedQuota === undefined || remainingSharedQuota > 0;
      const restingPrice = sharedPoolHasRoom
        && campaignPerUnitDiscount > 0
        && campaignPerUnitDiscount < unitPrice
        ? unitPrice - campaignPerUnitDiscount
        : fallbackPrice;
      lastRowPriceRef.current[priceKey] = restingPrice;
      return restingPrice;
    }

    const coveredQuantity = getAppliedPromoDiscountQuantity(productCode, quantity);
    if (coveredQuantity === undefined) {
      return lastRowPriceRef.current[priceKey] ?? fallbackPrice;
    }

    // "Price / seat" shows the standard (non-prorated) promo rate, matching the resting
    // price and other rows. On a co-term expansion the preview's discount is prorated
    // (e.g. $793.28 not $800), so take the covered quantity from the preview but the
    // per-seat discount from the campaign, which is never prorated.
    const campaignPerUnitDiscount = getGatedCampaignTier(feature, productCode)
      ?.perUnitDiscount ?? 0;
    const promoCoversSelection = coveredQuantity >= quantity
      && campaignPerUnitDiscount > 0
      && campaignPerUnitDiscount < unitPrice;
    const price = promoCoversSelection
      ? unitPrice - campaignPerUnitDiscount
      : unitPrice;
    lastRowPriceRef.current[priceKey] = price;
    return price;
  };
  const trueSubscriptions = useMemo(
    () => accountSubscriptions.filter(
      sub => sub.source === SubscriptionSource.RECURLY
        && TRUE_SUBSCRIPTION_FEATURES.includes(sub.feature),
    ),
    [accountSubscriptions],
  );
  const recurlySubscriptions = useMemo(
    () => accountSubscriptions.filter(sub => sub.source === SubscriptionSource.RECURLY),
    [accountSubscriptions],
  );
  const selfServeBundleSubscriptions = useMemo(
    () => trueSubscriptions.filter(
      sub => isSelfServePaidSubscription(sub)
        && TRUE_BUNDLE_PURCHASE_FEATURES.includes(sub.feature),
    ),
    [trueSubscriptions],
  );
  // --- True Bundle Starters (Decision 3, 6, 15, 17) -----------------------------------------
  // The package contents and the sell-until date come from LaunchDarkly `trueBundleConfig`, the
  // SAME flag the BE validates against: no entry (or a past `sellUntil`) hides the card, which is
  // the only rollout switch there is.
  const getBundleConfig = useCallback(
    (feature: Feature): TrueBundleConfig | undefined => getTrueBundleConfig(
      flags?.trueBundleConfig,
      feature,
    ),
    [flags?.trueBundleConfig],
  );
  // An account that already owns a bundle cannot buy a second one (any source: a direct-sale
  // bundle locks the online ones too), so every Starter card disappears.
  const hasBundleSubscription = useMemo(
    () => accountSubscriptions.some(sub => isTrueBundleFeature(sub.feature)),
    [accountSubscriptions],
  );
  // Starter-eligible account = "Choose your plan" layout (banner + cards); see the hook.
  // While `pending` the eligibility verdict is still in flight, so neither layout is drawn yet.
  const {
    plans: starterBundleRows,
    pending: starterLayoutPending,
  } = useStarterLayoutState(accountId);
  const isStarterLayout = starterBundleRows.length > 0;
  // While the Starter cards are on screen, "More testing plans" only shows when ops turn the LD
  // flag on (missing flag = hidden). Every other account keeps the table unconditionally.
  const showMoreTestingPlans = !isStarterLayout || !!flags?.moreTestingPlansWithStartersEnabled;
  // Serialised so the preview effect below re-runs when a card appears or disappears without
  // depending on a fresh array identity on every render.
  const starterBundleRowKey = starterBundleRows.map(plan => plan.feature).join(',');
  const isBundleSelected = (feature: Feature) => (planSeats[feature] ?? 0) > 0;

  const getBundleSubscriptionForFeature = useCallback(
    (feature: Feature) => getTargetSelfServeSubscription(
      selfServeBundleSubscriptions,
      feature,
      targetSubscriptionId,
    ),
    [selfServeBundleSubscriptions, targetSubscriptionId],
  );
  // A brand-new buyer (no paid Recurly subscription at all, self-serve or
  // direct-sale) has no existing interval to inherit, so the whole page follows
  // the cycle the incoming link asked for: when every cycle param in the URL
  // agrees, that cycle becomes the default for *all* rows, including the ones
  // the link never mentioned. Mixed monthly+annual params, or any paid
  // subscription on the account, fall back to the per-row default (annual).
  const hasPaidSubscription = useMemo(
    () => recurlySubscriptions.some(isPaidRecurlySubscription),
    [recurlySubscriptions],
  );
  const uniformCycleParam = useMemo(
    () => getUniformCycleParam(cycleParamByFeature),
    [cycleParamByFeature],
  );
  const defaultInterval = !hasPaidSubscription && uniformCycleParam !== undefined
    ? uniformCycleParam
    : PlanInterval.YEAR;
  // R6 (Decision 16): the retired True Automation / True Platform quota params are read by
  // nobody, so an old pricing-page link (`?trueAutomationQuota=1`) is ignored in silence instead
  // of seeding a row that is gone. The Starter bundles ARE seeded from their own quota param
  // ("Repurchase these items" on a bundle order) but only ever as the whole package: quantity 1.
  const [planSeats, setPlanSeats] = useState<Partial<Record<Feature, number>>>(() => (
    TRUE_BUNDLE_STARTER_OPTIONS.reduce<Partial<Record<Feature, number>>>((seats, plan) => (
      (bundleQuotaParamByFeature[plan.feature] ?? 0) > 0
        ? { ...seats, [plan.feature]: 1 }
        : seats
    ), {})
  ));
  const [testingProductSeats, setTestingProductSeats] = useState<Partial<Record<Feature, number>>>({
    [Feature.PER_USER_KSE]: studioSeatsParam ?? 0,
    [Feature.UNLIMITED_ENGINE]: runtimeSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]: crossBrowserSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]: mobileAppSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_LIVE_TESTING]: liveTestingSeatsParam ?? 0,
    [Feature.TESTOPS_G3]: testOpsSeatsParam ?? 0,
  });
  // A Starter bundle is in THIS order: picked on its card, or already in the request the preview
  // and checkout send. Katalon Studio cannot join that order (see `isBlockedByTrueBundleCart`):
  // its row cannot add licenses and its quantity counts as 0 everywhere - row, preview request,
  // Order Summary - whatever the state or a URL param says.
  const isStarterBundleInCart = isTrueBundleInCart
    || starterBundleRows.some(plan => isBundleSelected(plan.feature));
  const isBlockedByBundleCart = (feature: Feature): boolean => isBlockedByTrueBundleCart(
    feature,
    isStarterBundleInCart,
  );
  const getTestingProductSeats = (feature: Feature): number => (
    isBlockedByBundleCart(feature) ? 0 : testingProductSeats[feature] ?? 0
  );
  const bundleBlockedHint = intl.formatMessage({ id: 'page.plan.v2.bundle.kse.not.combinable' });
  // An annual subscription locks the row's billing-cycle switch on annual (annual -> monthly is a
  // downgrade the BE rejects), so such a row is annual whatever cycle the incoming link asks for.
  const hasAnnualPaidSubForFeature = useCallback(
    (feature: Feature): boolean => recurlySubscriptions.some(
      sub => sub.feature === feature && sub.billingInterval === PlanInterval.YEAR,
    ),
    [recurlySubscriptions],
  );
  // Initialise each SKU's interval from (1) the URL cycle param, then (2) the
  // existing self-serve subscription's interval, then (3) annual. Defaulting a
  // SKU that already has a monthly subscription straight to annual would make the
  // preview effect emit a spurious "monthly -> annual switch" request that then
  // leaks into the URL params and the confirmation page. A row locked on annual
  // ignores the URL param: a pricing-page `billing=monthly` link would otherwise
  // show (and preview) monthly on a switch the customer cannot change. Lazy init
  // so it only reads the subscriptions once.
  const resolveTestingInterval = (
    feature: Feature,
    cycleParam?: PlanInterval,
  ): PlanInterval => (hasAnnualPaidSubForFeature(feature) ? PlanInterval.YEAR : cycleParam)
    ?? getTargetSelfServeSubscription(
      recurlySubscriptions,
      feature,
      targetSubscriptionId,
    )?.billingInterval
    ?? defaultInterval;
  const [planIntervals, setPlanIntervals] = useState<FeatureIntervals>({});
  const [testingProductIntervals, setTestingProductIntervals] = useState<FeatureIntervals>(() => ({
    [Feature.PER_USER_KSE]: resolveTestingInterval(Feature.PER_USER_KSE, studioCycleParam),
    [Feature.UNLIMITED_ENGINE]: resolveTestingInterval(Feature.UNLIMITED_ENGINE, runtimeCycleParam),
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]:
      resolveTestingInterval(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, crossBrowserCycleParam),
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]:
      resolveTestingInterval(Feature.TESTCLOUD_SESSION_MOBILE_APP, mobileAppCycleParam),
    [Feature.TESTCLOUD_SESSION_LIVE_TESTING]:
      resolveTestingInterval(Feature.TESTCLOUD_SESSION_LIVE_TESTING, liveTestingCycleParam),
    [Feature.TESTOPS_G3]: resolveTestingInterval(Feature.TESTOPS_G3, testOpsCycleParam),
  }));
  const intervalSyncedRef = useRef(false);
  const getPlanInterval = (feature: Feature): PlanInterval => planIntervals[feature]
    ?? PlanInterval.YEAR;
  const getTestingInterval = (feature: Feature): PlanInterval => testingProductIntervals[feature]
    ?? PlanInterval.YEAR;
  const getFeatureInterval = (feature: Feature): PlanInterval => (
    TRUE_BUNDLE_PURCHASE_FEATURES.includes(feature)
      ? getPlanInterval(feature)
      : getTestingInterval(feature)
  );
  useEffect(() => {
    if (!activePromotion) {
      dispatch(fromPromotion.doFetchActivePromotion());
    }
  }, [activePromotion, dispatch]);

  useEffect(() => {
    if (activeCampaigns === undefined) {
      dispatch(fromPromotion.doFetchActiveCampaigns());
    }
  }, [activeCampaigns, dispatch]);

  // The new-account verdict comes from the BE (same function that guards the purchase); the
  // thunk itself makes sure it is asked at most once per store.
  useEffect(() => {
    if (accountId) {
      dispatch(fromSubscription.doCheckNewAccount(accountId));
    }
  }, [accountId, dispatch]);

  useEffect(() => {
    setTestingProductSeats(current => {
      let next = current;
      const syncQuota = (feature: Feature, quota: number | undefined) => {
        if (quota === undefined || current[feature] === quota) {
          return;
        }
        if (next === current) {
          next = { ...current };
        }
        next[feature] = quota;
      };

      syncQuota(Feature.PER_USER_KSE, studioSeatsParam);
      syncQuota(Feature.UNLIMITED_ENGINE, runtimeSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, crossBrowserSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_MOBILE_APP, mobileAppSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_LIVE_TESTING, liveTestingSeatsParam);
      syncQuota(Feature.TESTOPS_G3, testOpsSeatsParam);
      return next;
    });
  }, [
    studioSeatsParam,
    runtimeSeatsParam,
    crossBrowserSeatsParam,
    mobileAppSeatsParam,
    liveTestingSeatsParam,
    testOpsSeatsParam,
  ]);

  const getTrackingPayloadForFeature = (
    feature: Feature,
    quantity?: number,
    intervalOverride?: PlanInterval,
  ): TrackingPayload => {
    const interval = intervalOverride ?? getFeatureInterval(feature);
    return {
      object: Feature.toString(feature),
      product_id: getProductId(feature),
      product_code: getManualPurchaseProductCode(feature, interval),
      billing_interval: PlanInterval.getPlanIntervalCode(interval),
      unit_price: getUnitPrice(
        flags?.subscriptionPlanPrice,
        feature,
        interval,
        'product',
      ),
      ...(quantity !== undefined ? { quantity } : {}),
    };
  };

  const trackPlanPageAction = (payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: 'true_bundle',
        ...payload,
        ...trackingCookies,
        cart_snapshot: cartSnapshot,
      },
    );
  };

  // Default each SKU's billing interval to its existing self-serve subscription
  // interval (unless the interval is pinned by a URL param). Runs once, after the
  // account subscriptions have loaded. Rows without their own subscription fall
  // back to `defaultInterval` rather than keeping the lazy-init value: the state
  // is initialised on the first render, when the store may still be empty and
  // `defaultInterval` therefore still the uniform-cycle one. Re-resolving here
  // corrects those rows once a paid subscription turns up.
  useEffect(() => {
    if (intervalSyncedRef.current
      || (recurlySubscriptions.length === 0 && selfServeBundleSubscriptions.length === 0)) {
      return;
    }
    intervalSyncedRef.current = true;

    const planUpdates: FeatureIntervals = {};
    TRUE_BUNDLE_PLAN_OPTIONS.forEach(plan => {
      if (cycleParamByFeature[plan.feature] !== undefined) {
        return;
      }
      const planSubscription = getBundleSubscriptionForFeature(plan.feature);
      planUpdates[plan.feature] = planSubscription?.billingInterval ?? defaultInterval;
    });
    setPlanIntervals(current => ({ ...current, ...planUpdates }));

    const testingUpdates: FeatureIntervals = {};
    TRUE_MORE_TESTING_PRODUCT_FEATURES.forEach(feature => {
      // Locked on annual even when the URL pins a cycle: the first render may have run before
      // the subscriptions loaded and seeded the row from a `billing=monthly` link.
      if (hasAnnualPaidSubForFeature(feature)) {
        testingUpdates[feature] = PlanInterval.YEAR;
        return;
      }
      if (cycleParamByFeature[feature] !== undefined) {
        return;
      }
      const productSubscription = getTargetSelfServeSubscription(
        recurlySubscriptions,
        feature,
        targetSubscriptionId,
      );
      testingUpdates[feature] = productSubscription?.billingInterval ?? defaultInterval;
    });
    setTestingProductIntervals(current => ({ ...current, ...testingUpdates }));
  }, [
    cycleParamByFeature,
    defaultInterval,
    getBundleSubscriptionForFeature,
    hasAnnualPaidSubForFeature,
    recurlySubscriptions,
    selfServeBundleSubscriptions,
    targetSubscriptionId,
  ]);

  useEffect(() => {
    TRUE_BUNDLE_PLAN_OPTIONS.forEach(plan => {
      const interval = getPlanInterval(plan.feature);
      const planSubscription = getBundleSubscriptionForFeature(plan.feature);
      const isMonthlyToAnnualSwitch = !!planSubscription?.recurlySubUuId
        && planSubscription.billingInterval === PlanInterval.MONTH
        && interval === PlanInterval.YEAR
        && (planSeats[plan.feature] ?? 0) === 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: getManualPurchaseProductCode(plan.feature, interval),
          quantity: planSeats[plan.feature] ?? 0,
          ...(planSubscription?.recurlySubUuId
            ? { recurlySubscriptionUuid: planSubscription.recurlySubUuId }
            : {}),
        },
        keepZeroQuantity: isMonthlyToAnnualSwitch,
      }));
    });

    const bundleSubscription = targetSubscriptionId
      ? selfServeBundleSubscriptions.find(sub => sub.id === targetSubscriptionId)
      : selfServeBundleSubscriptions[0];
    if (bundleSubscription) {
      const subscribedPlan = TRUE_BUNDLE_PLAN_OPTIONS.find(
        plan => plan.feature === bundleSubscription.feature,
      );
      if (subscribedPlan) {
        dispatch(fromOrder.setTruePlatformPreviewRequest({
          productCode: getManualPurchaseProductCode(
            subscribedPlan.feature,
            getPlanInterval(subscribedPlan.feature),
          ),
          quantity: planSeats[bundleSubscription.feature] ?? 0,
          ...(bundleSubscription.recurlySubUuId
            ? { recurlySubscriptionUuid: bundleSubscription.recurlySubUuId }
            : {}),
        }));
      }
    }
  }, [
    planIntervals,
    dispatch,
    getBundleSubscriptionForFeature,
    planSeats,
    selfServeBundleSubscriptions,
    targetSubscriptionId,
  ]);

  useEffect(() => {
    // A hidden row (TestOps before its price key is seeded) must not reach the preview.
    TRUE_MORE_TESTING_PRODUCT_FEATURES.filter(isTestingFeatureEnabled).forEach(feature => {
      const interval = getTestingInterval(feature);
      const productSubscription = getTargetSelfServeSubscription(
        recurlySubscriptions,
        feature,
        targetSubscriptionId,
      );
      const isBlocked = isBlockedByTrueBundleCart(feature, isStarterBundleInCart);
      const quantity = isBlocked ? 0 : testingProductSeats[feature] ?? 0;
      // Switching an existing monthly subscription to annual without adding seats still needs a
      // preview (the plan change is billable). Keep the zero-quantity product in the request so the
      // preview fires, mirroring the bundle-plan effect above. A SKU kept out of a bundle order
      // (Katalon Studio) must not reach the order through that switch either.
      const isMonthlyToAnnualSwitch = !isBlocked
        && !!productSubscription?.recurlySubUuId
        && productSubscription.billingInterval === PlanInterval.MONTH
        && interval === PlanInterval.YEAR
        && quantity === 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: `${getProductId(feature)}_${PlanInterval.getPlanIntervalCode(interval)}`,
          quantity,
          ...(productSubscription?.recurlySubUuId
            ? { recurlySubscriptionUuid: productSubscription.recurlySubUuId }
            : {}),
        },
        keepZeroQuantity: isMonthlyToAnnualSwitch,
      }));
    });
  }, [
    testingProductIntervals,
    dispatch,
    recurlySubscriptions,
    targetSubscriptionId,
    testingProductSeats,
    testOpsRowEnabled,
    isStarterBundleInCart,
  ]);

  // The Starter bundles ride the same preview request as every other row, but as ONE package:
  // quantity is always 1, the add-on set comes from `trueBundleConfig` (keyed by the BE enum
  // NAME, which is what `SingleProductPreviewRequest.addOns` deserialises), and no
  // `recurlySubscriptionUuid` is ever attached - a bundle is never expanded or switched.
  useEffect(() => {
    TRUE_BUNDLE_STARTER_OPTIONS.forEach(plan => {
      const config = getBundleConfig(plan.feature);
      const isVisible = starterBundleRowKey.split(',').includes(plan.feature);
      const isSelected = isVisible && !!config && (planSeats[plan.feature] ?? 0) > 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: getManualPurchaseProductCode(plan.feature, PlanInterval.YEAR),
          quantity: isSelected ? 1 : 0,
          ...(isSelected ? { addOns: getTrueBundleAddOnsPayload(config) } : {}),
        },
      }));
    });
  }, [dispatch, getBundleConfig, planSeats, starterBundleRowKey]);

  // Exactly one bundle per order: the BE answers `promotion.multiple.campaigns.in.order` when two
  // Starter campaigns land in the same order, and picking a second package is never the intent.
  // Picking a bundle is the latest, deliberate choice, so it also wins over Katalon Studio: the
  // licenses already in the cart are dropped (KSE is bought in a separate order).
  const toggleBundleSelection = (feature: Feature) => () => {
    const wasSelected = isBundleSelected(feature);
    trackPlanPageAction({
      field: 'true_bundle_starter',
      action: wasSelected ? 'remove_bundle' : 'select_bundle',
      ...getTrackingPayloadForFeature(feature, wasSelected ? 0 : 1, PlanInterval.YEAR),
    });
    if (!wasSelected) {
      setTestingProductSeats(current => (
        (current[Feature.PER_USER_KSE] ?? 0) > 0
          ? { ...current, [Feature.PER_USER_KSE]: 0 }
          : current
      ));
    }
    setPlanSeats(current => ({
      ...current,
      ...Object.fromEntries(
        TRUE_BUNDLE_STARTER_OPTIONS.map(plan => [plan.feature, 0]),
      ),
      [feature]: wasSelected ? 0 : 1,
    }));
  };

  const updatePlanSeats = (feature: Feature) => (quota: number) => {
    const previousQuota = planSeats[feature] ?? 0;
    if (previousQuota !== quota) {
      trackPlanPageAction({
        field: 'true_bundle_plan',
        action: 'change_quantity',
        previous_quantity: previousQuota,
        ...getTrackingPayloadForFeature(feature, quota),
      });
    }

    setPlanSeats(current => ({
      ...current,
      [feature]: quota,
    }));
  };

  const updateTestingProductSeats = (feature: Feature) => (quota: number) => {
    if (isBlockedByBundleCart(feature)) {
      return;
    }
    const previousQuota = testingProductSeats[feature] ?? 0;
    if (previousQuota !== quota) {
      trackPlanPageAction({
        field: 'more_testing_plan',
        action: 'change_quantity',
        previous_quantity: previousQuota,
        ...getTrackingPayloadForFeature(feature, quota),
      });
    }

    setTestingProductSeats(current => ({
      ...current,
      [feature]: quota,
    }));
  };

  const updatePlanInterval = (feature: Feature) => (interval: PlanInterval) => {
    if (getPlanInterval(feature) === interval) {
      return;
    }
    trackPlanPageAction({
      field: 'billing_interval',
      action: 'change_interval',
      ...getTrackingPayloadForFeature(feature, planSeats[feature] ?? 0, interval),
    });
    setPlanIntervals(current => ({
      ...current,
      [feature]: interval,
    }));
  };

  const updateTestingProductInterval = (feature: Feature) => (interval: PlanInterval) => {
    if (getTestingInterval(feature) === interval) {
      return;
    }
    trackPlanPageAction({
      field: 'billing_interval',
      action: 'change_interval',
      ...getTrackingPayloadForFeature(feature, testingProductSeats[feature] ?? 0, interval),
    });
    setTestingProductIntervals(current => ({
      ...current,
      [feature]: interval,
    }));
  };

  // `blockedHint` = the row cannot join this order: the button is disabled and the hint explains
  // why on hover (wrapped in a span, a disabled button fires no mouse events).
  const renderAddButton = (onAdd: () => void, blockedHint?: string) => {
    const button = (
      <Box
        component="button"
        type="button"
        onClick={blockedHint ? undefined : onAdd}
        disabled={!!blockedHint}
        className={classes.addSeatButton}
      >
        +
        {' '}
        <FormattedMessage id="page.plan.v2.true.bundle.add" />
      </Box>
    );

    return blockedHint ? (
      <Tooltip disableInteractive title={blockedHint} placement="top">
        <Box component="span" className={classes.addSeatButtonHintAnchor}>
          {button}
        </Box>
      </Tooltip>
    ) : button;
  };

  // "More testing plans" rows sold on this env (TestOps only once its price key is seeded). Once a
  // bundle is owned, Katalon Studio moves here too - right after TestOps - and the plan table goes.
  const enabledTestingPlanOptions = TRUE_TESTING_PLAN_OPTIONS
    .flatMap(option => (hasBundleSubscription && option.feature === Feature.TESTOPS_G3
      ? [option, KSE_TESTING_PLAN_OPTION]
      : [option]))
    .filter(option => isTestingFeatureEnabled(option.feature));

  // ---- "Choose your plan" (Starter-eligible account, design 23.9) ---------------------------
  // Three vertical cards, single-select: Katalon Studio (KS Starter seats) | True Manual Starter |
  // True Automation Starter. Picking one card drops the other two - a bundle drops the Studio
  // licenses (toggleBundleSelection), Studio drops the bundle (selectStudioStarter).
  const isAnyStarterBundleSelected = starterBundleRows.some(plan => isBundleSelected(plan.feature));
  const studioSeats = getTestingProductSeats(Feature.PER_USER_KSE);
  const isStudioCardSelected = !isAnyStarterBundleSelected && studioSeats > 0;
  const starterTermsNote = intl.formatMessage({ id: 'page.plan.v2.starter.card.saving.terms' });
  const formatPrice = (amount: number) => formatCurrency(
    intl,
    amount,
    DEFAULT_CURRENCY_FORMAT_OPTIONS,
  );

  // Katalon Studio seats from the Studio card. Unlike updateTestingProductSeats it is not stopped
  // by a picked bundle: choosing Studio is the latest, deliberate choice, so the bundle goes.
  const selectStudioStarter = (seats: number) => {
    trackPlanPageAction({
      field: 'starter_card',
      action: isStudioCardSelected ? 'change_quantity' : 'select_starter',
      previous_quantity: studioSeats,
      ...getTrackingPayloadForFeature(Feature.PER_USER_KSE, seats),
    });
    if (isAnyStarterBundleSelected) {
      setPlanSeats(current => ({
        ...current,
        ...Object.fromEntries(TRUE_BUNDLE_STARTER_OPTIONS.map(plan => [plan.feature, 0])),
      }));
    }
    setTestingProductSeats(current => ({
      ...current,
      [Feature.PER_USER_KSE]: seats,
    }));
  };

  const toggleStudioCard = () => {
    if (isStudioCardSelected) {
      updateTestingProductSeats(Feature.PER_USER_KSE)(0);
      return;
    }
    selectStudioStarter(Math.max(studioSeats, 1));
  };

  const changeStudioInterval = (interval: PlanInterval) => {
    updateTestingProductInterval(Feature.PER_USER_KSE)(interval);
    if (!isStudioCardSelected) {
      selectStudioStarter(Math.max(studioSeats, 1));
    }
  };

  // Display-only list price behind a card's saving badge: LD `<prefix>ListAnnually` when seeded,
  // else the plan's constant (see TrueBundlePlan.listPriceAnnually).
  const getCardListPrice = (plan: TrueBundlePlan): number | undefined => {
    const listPriceKey = `${Feature.getFeaturePrefix(plan.feature)}ListAnnually` as keyof SubscriptionPlanPrice;
    const ldListPrice = flags?.subscriptionPlanPrice?.[listPriceKey];
    return typeof ldListPrice === 'number' ? ldListPrice : plan.listPriceAnnually;
  };

  // Package price of a Starter bundle card: eCom subtotal (LD `trueBundle*Annually`) minus the
  // campaign that discounts THIS bundle - the same campaign the BE applies at checkout. With no
  // campaign the package rests on its eCom subtotal; while the campaigns are still loading the
  // published offer price from LaunchDarkly stands in, so the card never flashes a price the
  // checkout will not charge.
  const getBundleCardData = (plan: TrueBundlePlan) => {
    const productCode = getManualPurchaseProductCode(plan.feature, PlanInterval.YEAR);
    const ecomSubtotal = getUnitPrice(
      flags?.subscriptionPlanPrice,
      plan.feature,
      PlanInterval.YEAR,
      'product',
    );
    const campaignTier = getCampaignTier(productCode, true);
    const publishedOfferPrice = getDiscountPrice(
      flags?.subscriptionPlanPrice,
      plan.feature,
      PlanInterval.YEAR,
    );
    const listPrice = getCardListPrice(plan);
    const cardPrice = getTrueBundleCardPrice({
      ecomSubtotal,
      campaignDiscount: campaignTier?.perUnitDiscount,
      publishedOfferPrice: activeCampaigns === undefined ? publishedOfferPrice : undefined,
      listPrice,
    });
    return cardPrice;
  };

  // Katalon Studio card: priced per seat by the KS Starter campaign (tiered, first N seats). The
  // card sells the alternative to the bundle cards - picking it drops the bundle - so it shows the
  // Starter price Studio gets once picked, even while a bundle is (still) the current choice.
  const getStudioCardData = () => {
    const feature = Feature.PER_USER_KSE;
    const interval = getTestingInterval(feature);
    const annualProductCode = `${getProductId(feature)}_${PlanInterval.getPlanIntervalCode(PlanInterval.YEAR)}`;
    const annualUnitPrice = getUnitPrice(
      flags?.subscriptionPlanPrice,
      feature,
      PlanInterval.YEAR,
      'product',
    );
    const offer = getGatedStarterPackageOffer(feature, annualProductCode, false);
    const tieredOffer = offer?.kind === 'tiered'
      && offer.perUnitDiscount > 0
      && offer.perUnitDiscount < annualUnitPrice
      ? offer
      : undefined;
    const listPrice = getCardListPrice(KSE_STUDIO_PLAN_ROW);
    const annual = getTrueBundleCardPrice({
      ecomSubtotal: annualUnitPrice,
      campaignDiscount: tieredOffer?.perUnitDiscount,
      listPrice,
    });
    const monthlyUnitPrice = interval === PlanInterval.MONTH
      ? resolveRowUnitPrice(
        feature,
        PlanInterval.MONTH,
        studioSeats,
        `${getProductId(feature)}_${PlanInterval.getPlanIntervalCode(PlanInterval.MONTH)}`,
      )
      : 0;
    return {
      interval,
      tieredOffer,
      listPrice,
      annualUnitPrice,
      annualSeatPrice: annual.annualPrice,
      savingPercent: annual.savingPercent,
      // Per seat per year (design 23.9: the card quotes the annual Starter rate as-is), or the
      // monthly list price while the toggle is on monthly billing.
      displayPrice: interval === PlanInterval.YEAR
        ? annual.annualPrice
        : monthlyUnitPrice,
    };
  };

  const renderStudioCard = () => {
    const studio = getStudioCardData();
    const isAnnual = studio.interval === PlanInterval.YEAR;
    const { productId } = KSE_STUDIO_PLAN_ROW;
    const savingTooltip: StarterCardSavingTooltip | undefined = studio.tieredOffer
      ? {
        title: intl.formatMessage(
          { id: 'page.plan.v2.starter.card.saving.title' },
          { percent: studio.savingPercent },
        ),
        items: [
          intl.formatMessage(
            { id: 'page.plan.v2.starter.card.saving.studio.first' },
            {
              count: studio.tieredOffer.seatCount,
              price: formatPrice(studio.annualSeatPrice),
              listPrice: formatPrice(studio.listPrice ?? studio.annualUnitPrice),
            },
          ),
          intl.formatMessage(
            { id: 'page.plan.v2.starter.card.saving.studio.next' },
            {
              count: studio.tieredOffer.seatCount + 1,
              price: formatPrice(studio.annualUnitPrice),
            },
          ),
          starterTermsNote,
        ],
      }
      : undefined;

    return (
      <StarterPlanCard
        key={Feature.PER_USER_KSE}
        id={`true-bundle-starter-${productId}`}
        title={intl.formatMessage({ id: 'page.plan.v2.true.testing.studio.title' })}
        description={intl.formatMessage({ id: 'page.plan.v2.starter.card.studio.description' })}
        selected={isStudioCardSelected}
        onToggle={toggleStudioCard}
        price={formatPrice(studio.displayPrice)}
        priceUnit={intl.formatMessage({
          id: isAnnual
            ? 'page.plan.v2.starter.card.price.seat.year'
            : 'page.plan.v2.starter.card.price.seat.month',
        })}
        savingLabel={isAnnual && studio.savingPercent > 0
          ? intl.formatMessage(
            { id: 'page.plan.v2.true.bundle.starter.saving' },
            { percent: studio.savingPercent },
          )
          : undefined}
        savingTooltip={savingTooltip}
        onSavingTooltipOpen={() => trackPlanPageAction({
          field: 'starter_card',
          action: 'view_tooltip',
          tooltip: 'saving',
          ...getTrackingPayloadForFeature(Feature.PER_USER_KSE, studioSeats),
        })}
        includedHref={intl.formatMessage({ id: 'page.plan.v2.starter.card.included.url.studio' })}
        onIncludedClick={() => trackPlanPageAction({
          field: 'starter_card',
          action: 'view_included',
          ...getTrackingPayloadForFeature(Feature.PER_USER_KSE, studioSeats),
        })}
      >
        <StarterCardControls className={classes.studioControls}>
          <Box component="label" className={classes.studioCycle}>
            <Switch
              size="small"
              checked={isAnnual}
              onChange={(_, checked) => changeStudioInterval(
                checked ? PlanInterval.YEAR : PlanInterval.MONTH,
              )}
              disabled={hasAnnualPaidSubForFeature(Feature.PER_USER_KSE)}
              inputProps={{ id: `true-bundle-starter-${productId}-annual` }}
              color="success"
            />
            <FormattedMessage id="page.plan.v2.starter.card.pay.annually" />
          </Box>
          <Box
            className={classes.studioSeats}
            aria-label={intl.formatMessage({ id: 'page.plan.v2.starter.card.seats' })}
            role="group"
          >
            <ProductIconBadge iconKey="studio" size={18} />
            <SelectQuota
              productId={`true-bundle-starter-${productId}`}
              initQuota={1}
              quota={Math.max(studioSeats, 1)}
              setQuota={selectStudioStarter}
              width={28}
              height={30}
            />
          </Box>
        </StarterCardControls>
        {studio.tieredOffer && (
          <Typography component="div" className={classes.studioNotes} id={`true-bundle-starter-${productId}-notes`}>
            {isAnnual ? (
              <>
                <span>
                  {intl.formatMessage(
                    { id: 'page.plan.v2.starter.card.studio.tier.first' },
                    {
                      count: studio.tieredOffer.seatCount,
                      price: formatPrice(studio.annualSeatPrice),
                    },
                  )}
                </span>
                <span>
                  {intl.formatMessage(
                    { id: 'page.plan.v2.starter.card.studio.tier.next' },
                    {
                      count: studio.tieredOffer.seatCount + 1,
                      price: formatPrice(studio.annualUnitPrice),
                    },
                  )}
                </span>
              </>
            ) : (
              <span>
                <FormattedMessage id="page.plan.v2.starter.card.studio.annual.only" />
              </span>
            )}
          </Typography>
        )}
      </StarterPlanCard>
    );
  };

  const renderBundleCard = (plan: TrueBundlePlan) => {
    const config = getBundleConfig(plan.feature);
    const components = getTrueBundleComponents(config);
    const { annualPrice, savingPercent } = getBundleCardData(plan);
    const selected = isBundleSelected(plan.feature);
    const trackingPayload = getTrackingPayloadForFeature(
      plan.feature,
      selected ? 1 : 0,
      PlanInterval.YEAR,
    );

    return (
      <StarterPlanCard
        key={plan.feature}
        id={`true-bundle-starter-${plan.productId}`}
        title={intl.formatMessage({ id: plan.titleId })}
        description={intl.formatMessage({ id: plan.descriptionId })}
        selected={selected}
        onToggle={toggleBundleSelection(plan.feature)}
        price={formatPrice(annualPrice)}
        priceUnit={intl.formatMessage({ id: 'page.plan.v2.true.bundle.starter.price.year' })}
        savingLabel={savingPercent > 0
          ? intl.formatMessage(
            { id: 'page.plan.v2.true.bundle.starter.saving' },
            { percent: savingPercent },
          )
          : undefined}
        savingTooltip={{
          title: intl.formatMessage(
            { id: 'page.plan.v2.starter.card.saving.title' },
            { percent: savingPercent },
          ),
          items: [
            intl.formatMessage(
              { id: 'page.plan.v2.starter.card.saving.bundle.price' },
              { seats: getTrueBundleSeatCount(components), price: formatPrice(annualPrice) },
            ),
            intl.formatMessage({ id: 'page.plan.v2.starter.card.saving.bundle.renew' }),
            starterTermsNote,
          ],
        }}
        onSavingTooltipOpen={() => trackPlanPageAction({
          field: 'true_bundle_starter',
          action: 'view_tooltip',
          tooltip: 'saving',
          ...trackingPayload,
        })}
        includedHref={intl.formatMessage({ id: 'page.plan.v2.starter.card.included.url.bundle' })}
        onIncludedClick={() => trackPlanPageAction({
          field: 'true_bundle_starter',
          action: 'view_included',
          ...trackingPayload,
        })}
      >
        {/* Components one per line: quantity + product, never a price (Decision 2). Built from
            `trueBundleConfig`, plus the free TestCloud Cross Browser the BE grants per seat. */}
        <StarterComponentList
          id={`true-bundle-starter-${plan.productId}-components`}
          lines={getTrueBundleCardChips(intl, components).map(chip => ({
            key: chip.key,
            quantity: chip.quantity,
            name: chip.name,
            icon: <ProductIconBadge iconKey={chip.iconKey} size={16} />,
          }))}
        />
      </StarterPlanCard>
    );
  };

  const renderStarterLayout = () => {
    // "Until October 31": the EARLIEST last day among the Starters on screen, so the banner never
    // promises a date one of the cards will not honour (an expired card simply disappears).
    const untilDate = starterBundleRows
      .map(plan => getTrueBundleSellUntilDate(getBundleConfig(plan.feature)))
      .filter((date): date is Date => !!date)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    const bannerSavingPercent = getStarterBannerSavingPercent([
      getStudioCardData().savingPercent,
      ...starterBundleRows.map(plan => getBundleCardData(plan).savingPercent),
    ]);

    return (
      <Box id="starter-plan-layout">
        <StarterOfferBanner
          untilDate={untilDate
            ? intl.formatDate(untilDate, { month: 'long', day: 'numeric' })
            : undefined}
          savingPercent={bannerSavingPercent}
        />
        <StarterCardGrid columns={starterBundleRows.length + 1}>
          {renderStudioCard()}
          {starterBundleRows.map(renderBundleCard)}
        </StarterCardGrid>
      </Box>
    );
  };

  // R6 (Decision 16) emptied TRUE_BUNDLE_PLAN_OPTIONS (True Automation / True Platform are
  // retired from self-serve); the rows on sale are Katalon Studio Enterprise plus whichever
  // Starter bundles `trueBundleConfig` currently offers this account. A bundle owner has no
  // Starter left to buy, so Katalon Studio moves to "More testing plans" and the table is empty.
  // A Starter-eligible account gets the "Choose your plan" cards instead (renderStarterLayout).
  const planTableRows = [
    ...(hasBundleSubscription ? [] : [KSE_STUDIO_PLAN_ROW]),
    ...TRUE_BUNDLE_PLAN_OPTIONS,
  ];

  // Which layout to draw is not known until the new-account verdict lands (a Starter IS on sale
  // for this account shape) — drawing the regular table now would flash and swap to the Starter
  // cards a beat later, so hold both behind a skeleton. Placed after every hook above.
  if (starterLayoutPending) {
    return (
      <Box id="true-bundle-plan-select-loading" pb={2}>
        <Skeleton variant="rounded" height={92} sx={{ mb: 3, borderRadius: 2 }} />
        <Box display="flex" gap={2}>
          <Skeleton variant="rounded" height={300} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={300} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={300} sx={{ flex: 1, borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box id="true-bundle-plan-select" pb={2}>
      {isStarterLayout && renderStarterLayout()}
      {/* ---- Plan table ---- */}
      {!isStarterLayout && planTableRows.length > 0 && (
      <Box className={classes.tableCard}>
        <Box className={classes.tableHeaderRow}>
          <Typography className={classes.tableHeaderCell}>
            <FormattedMessage id="page.plan.v2.table.header.plan" />
          </Typography>
          <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
            <FormattedMessage id="page.plan.v2.billing.cycle" />
          </Typography>
          <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
            <FormattedMessage id="page.plan.v2.table.header.seats" />
          </Typography>
          <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellEnd}`}>
            <FormattedMessage id="page.plan.v2.table.header.price.short" />
          </Typography>
        </Box>

        {planTableRows.map((plan, index) => {
          // KSE renders in the plan table but keeps its "more testing products"
          // seats/interval state and preview product-code path.
          const isTestingProduct = plan.feature === Feature.PER_USER_KSE;
          const seats = isTestingProduct
            ? getTestingProductSeats(plan.feature)
            : planSeats[plan.feature] ?? 0;
          const rowBlockedHint = isBlockedByBundleCart(plan.feature)
            ? bundleBlockedHint
            : undefined;
          const interval = getFeatureInterval(plan.feature);
          const updateSeats = isTestingProduct
            ? updateTestingProductSeats(plan.feature)
            : updatePlanSeats(plan.feature);
          const updateInterval = isTestingProduct
            ? updateTestingProductInterval(plan.feature)
            : updatePlanInterval(plan.feature);
          const trackingField = isTestingProduct ? 'more_testing_plan' : 'true_bundle_plan';
          const rowProductCode = isTestingProduct
            ? `${getProductId(plan.feature)}_${PlanInterval.getPlanIntervalCode(interval)}`
            : getManualPurchaseProductCode(plan.feature, interval);
          const effectivePrice = resolveRowUnitPrice(plan.feature, interval, seats, rowProductCode);
          // Starter package (only matches while the row is on the annual cycle — the
          // campaign discounts the annual product code). A tiered offer advertises its
          // tier pricing directly on the row; a one-time offer sells through the
          // claim band and shows the blended rate once claimed.
          const starterOffer = getGatedStarterPackageOffer(plan.feature, rowProductCode);
          const tieredOffer = starterOffer?.kind === 'tiered' ? starterOffer : undefined;
          const onetimeOffer = starterOffer?.kind === 'onetime' ? starterOffer : undefined;
          const rowUnitPrice = getUnitPrice(
            flags?.subscriptionPlanPrice,
            plan.feature,
            interval,
            'product',
          );
          const starterPackagePrice = starterOffer
            ? getStarterPackagePrice(starterOffer, rowUnitPrice)
            : 0;
          const starterApplied = !!onetimeOffer && seats >= onetimeOffer.seatCount;
          // The preview can apply the package without any new seats selected here —
          // e.g. a monthly→annual switch of an existing subscription that redeems the
          // full quota. The offer is already in the cart then, so the claim band must
          // not sell it again (clicking it would pile 5 MORE seats on top).
          const starterAppliedInPreview = !!onetimeOffer
            && (orderPreviewResponse?.productsPreview ?? []).some(
              product => product.appliedPromotion?.campaignCode === onetimeOffer.campaignCode
                && isSamePlanIgnoringEcom(product.productCode, onetimeOffer.productCode)
                && product.appliedPromotion.discountAmount > 0,
            );
          // While that switch preview is still in flight the response above is absent
          // or stale, so the band would flash before hiding. A row with an existing
          // monthly subscription on the annual cycle always previews as a switch —
          // hold the band back until the preview has settled.
          const rowSubscription = getTargetSelfServeSubscription(
            recurlySubscriptions,
            plan.feature,
            targetSubscriptionId,
          );
          const pendingSwitchToAnnual = !!rowSubscription?.recurlySubUuId
            && rowSubscription.billingInterval === PlanInterval.MONTH
            && interval === PlanInterval.YEAR;
          const previewSettled = !previewLoading && hasPreviewResponseForRequests(
            getTrueBundlePreviewRequests(orderPreviewRequest, fallbackPreviewRequest),
            orderPreviewResponse,
          );
          const showStarterBand = !!onetimeOffer
            && !rowBlockedHint
            && !starterApplied
            && !starterAppliedInPreview
            && (!pendingSwitchToAnnual || previewSettled);
          const rowIntervalSuffix = intl.formatMessage({
            id: interval === PlanInterval.YEAR
              ? 'page.plan.v2.true.testing.price.year'
              : 'page.plan.v2.true.testing.price.month',
          });
          const claimStarterOffer = () => {
            if (!onetimeOffer) {
              return;
            }
            trackPlanPageAction({
              field: trackingField,
              action: 'claim_starter_offer',
              ...getTrackingPayloadForFeature(plan.feature, onetimeOffer.seatCount),
            });
            updateSeats(onetimeOffer.seatCount);
          };

          return (
            <Box key={plan.feature}>
              {index > 0 && <Box className={classes.tableRowDivider} />}
              <Box className={classes.tableGridRow}>
                {/* Plan */}
                <Box className={classes.planMain}>
                  <Box className={classes.planTitleRow}>
                    <Tooltip
                      disableInteractive
                      placement="bottom-start"
                      onOpen={() => trackPlanPageAction({
                        field: trackingField,
                        action: 'view_tooltip',
                        tooltip: 'plan_details',
                        ...getTrackingPayloadForFeature(plan.feature, seats),
                      })}
                      classes={{
                        tooltip: classes.planInfoTooltip,
                      }}
                      title={(
                        <Box>
                          <Typography className={classes.planInfoTooltipTitle}>
                            {intl.formatMessage({ id: plan.tooltipTitleId })}
                          </Typography>
                          <ul className={classes.tooltipList}>
                            {plan.benefitIds.map(benefitId => (
                              <li key={benefitId}>{intl.formatMessage({ id: benefitId })}</li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    >
                      <Typography component="span" className={classes.planNameDashed}>
                        <FormattedMessage id={plan.titleId} />
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box className={classes.planProducts} sx={{ ml: 0, mt: 1 }}>
                    {plan.productLabelIds.map(labelId => {
                      const productIconKey = getPlanProductIconKey(labelId);
                      const productLabel = intl.formatMessage({ id: labelId });

                      return (
                        <Tooltip
                          disableInteractive
                          key={labelId}
                          title={productLabel}
                          placement="top-start"
                          classes={{
                            tooltip: classes.productIconTooltip,
                          }}
                          onOpen={() => trackPlanPageAction({
                            field: 'true_bundle_product_icon',
                            object: productLabel,
                            action: 'view_tooltip',
                            parent_object: Feature.toString(plan.feature),
                            label_id: labelId,
                            billing_interval: PlanInterval.getPlanIntervalCode(interval),
                          })}
                        >
                          <Box
                            aria-label={productLabel}
                            className={classes.productIconBadge}
                            component="span"
                            role="img"
                          >
                            <ProductIconBadge iconKey={productIconKey} />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>

                {/* Billing cycle */}
                <Box className={classes.cellStart}>
                  <PlanBillingSwitch
                    interval={interval}
                    onChange={updateInterval}
                    disabled={hasAnnualPaidSubForFeature(plan.feature)}
                  />
                </Box>

                {/* Add seats */}
                <Box className={classes.cellSeats}>
                  {seats === 0 ? renderAddButton(() => updateSeats(1), rowBlockedHint) : (
                    <SelectQuota
                      productId={getProductId(plan.feature)}
                      initQuota={0}
                      quota={seats}
                      setQuota={updateSeats}
                      maxQuota={!isTestingProduct && MAX_SEATS ? Number(MAX_SEATS) : undefined}
                      width={30}
                      height={32}
                    />
                  )}
                </Box>

                {/* Price */}
                <Box className={classes.priceCell}>
                  <Box className={classes.priceCellStack}>
                    <Typography component="span" className={classes.priceCellAmount}>
                      {(starterApplied || !!tieredOffer) && (
                        <>
                          <FormattedMessage id="page.plan.v2.true.bundle.starter.from" />
                          {' '}
                        </>
                      )}
                      {formatCurrency(intl, effectivePrice, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                    </Typography>
                    {/* A Starter offer's tier notes already spell out the unit rate. */}
                    {!tieredOffer && !starterApplied && (
                      <Typography component="span" className={classes.priceUnit}>
                        {intl.formatMessage(
                          {
                            id: interval === PlanInterval.YEAR
                              ? 'page.plan.v2.true.testing.price.unit.year'
                              : 'page.plan.v2.true.testing.price.unit.month',
                          },
                          { unit: isTestingProduct ? 'license' : 'seat' },
                        )}
                      </Typography>
                    )}
                    {tieredOffer && (
                      <Box className={classes.starterTierNotes}>
                        <span>
                          {intl.formatMessage(
                            { id: 'page.plan.v2.true.bundle.starter.tiered.note' },
                            {
                              quantityLabel: getSummaryQuantityLabel(
                                plan.feature,
                                tieredOffer.seatCount,
                              ),
                              price: formatCurrency(
                                intl,
                                rowUnitPrice - tieredOffer.perUnitDiscount,
                                DEFAULT_CURRENCY_FORMAT_OPTIONS,
                              ),
                              interval: rowIntervalSuffix,
                            },
                          )}
                        </span>
                        <span>
                          {intl.formatMessage(
                            { id: 'page.plan.v2.true.bundle.starter.tiered.note2' },
                            {
                              count: tieredOffer.seatCount + 1,
                              price: formatCurrency(
                                intl,
                                rowUnitPrice,
                                DEFAULT_CURRENCY_FORMAT_OPTIONS,
                              ),
                              interval: rowIntervalSuffix,
                            },
                          )}
                        </span>
                      </Box>
                    )}
                    {starterApplied && starterOffer && (
                      <Box className={classes.starterTierNotes}>
                        <span>
                          {intl.formatMessage(
                            { id: 'page.plan.v2.true.bundle.starter.tier.note' },
                            {
                              quantityLabel: getSummaryQuantityLabel(
                                plan.feature,
                                starterOffer.seatCount,
                              ),
                              price: formatCurrency(
                                intl,
                                starterPackagePrice,
                                DEFAULT_CURRENCY_FORMAT_OPTIONS,
                              ),
                              interval: rowIntervalSuffix,
                            },
                          )}
                        </span>
                        <span>
                          {intl.formatMessage(
                            { id: 'page.plan.v2.true.bundle.starter.tier.note2' },
                            {
                              unit: getSummaryUnitWord(plan.feature, 2),
                              price: formatCurrency(
                                intl,
                                rowUnitPrice,
                                DEFAULT_CURRENCY_FORMAT_OPTIONS,
                              ),
                              interval: rowIntervalSuffix,
                            },
                          )}
                        </span>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {showStarterBand && starterOffer && (
                <Box className={classes.starterBand}>
                  <Typography component="span" className={classes.starterBandText}>
                    <Box component="span" className={classes.starterBandTitle}>
                      <FormattedMessage id="page.plan.v2.true.bundle.starter.tag" />
                      {' — '}
                    </Box>
                    {intl.formatMessage(
                      { id: 'page.plan.v2.true.bundle.starter.offer' },
                      {
                        quantityLabel: getSummaryQuantityLabel(
                          plan.feature,
                          starterOffer.seatCount,
                        ),
                        price: formatCurrency(
                          intl,
                          starterPackagePrice,
                          DEFAULT_CURRENCY_FORMAT_OPTIONS,
                        ),
                        interval: rowIntervalSuffix,
                      },
                    )}
                  </Typography>
                  <Box
                    component="button"
                    type="button"
                    className={classes.starterClaimButton}
                    onClick={claimStarterOffer}
                  >
                    <FormattedMessage id="page.plan.v2.true.bundle.starter.cta" />
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        <Box className={classes.includedNote}>
          <FormattedMessage id="page.plan.v2.true.bundle.discounts.note" />
        </Box>
      </Box>
      )}

      {showMoreTestingPlans && (
      <Box className={classes.testingPlansSection}>
        <Box className={classes.testingCard}>
          <Typography className={classes.testingTitle}>
            <FormattedMessage id="page.plan.v2.true.bundle.more.testing.plans" />
          </Typography>
          <Box className={classes.testingHeaderRow}>
            <Typography className={classes.tableHeaderCell}>
              <FormattedMessage id="page.plan.v2.table.header.product" />
            </Typography>
            <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
              <FormattedMessage id="page.plan.v2.billing.cycle" />
            </Typography>
            <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
              <FormattedMessage id="page.plan.v2.table.header.quantity" />
            </Typography>
            <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellEnd}`}>
              <FormattedMessage id="page.plan.v2.table.header.price.short" />
            </Typography>
          </Box>

          {enabledTestingPlanOptions.map((option, index) => {
            const quota = getTestingProductSeats(option.feature);
            const interval = getTestingInterval(option.feature);
            const effectivePrice = resolveRowUnitPrice(
              option.feature,
              interval,
              quota,
              `${getProductId(option.feature)}_${PlanInterval.getPlanIntervalCode(interval)}`,
            );
            const addButtonBlockedHint = isBlockedByBundleCart(option.feature)
              ? bundleBlockedHint
              : undefined;

            return (
              <Box key={option.feature}>
                {index > 0 && <Box className={classes.tableRowDivider} />}
                <Box className={classes.testingGridRow}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.375, minWidth: 0 }}>
                    <ProductIconBadge iconKey={option.iconKey} size={20} />
                    <Tooltip
                      disableInteractive
                      title={(
                        <ul className={classes.tooltipList}>
                          {option.infoIds.map(infoId => (
                            <li key={infoId}>{intl.formatMessage({ id: infoId })}</li>
                          ))}
                        </ul>
                      )}
                      placement="bottom-start"
                      classes={{
                        tooltip: classes.testingInfoTooltip,
                      }}
                      onOpen={() => trackPlanPageAction({
                        field: 'more_testing_plan',
                        action: 'view_tooltip',
                        tooltip: 'plan_details',
                        ...getTrackingPayloadForFeature(option.feature, quota),
                      })}
                    >
                      <Typography component="span" className={classes.testingNameDashed}>
                        <FormattedMessage id={option.titleId} />
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Box className={classes.cellStart}>
                    <PlanBillingSwitch
                      interval={interval}
                      onChange={updateTestingProductInterval(option.feature)}
                      disabled={hasAnnualPaidSubForFeature(option.feature)}
                    />
                  </Box>

                  <Box className={classes.cellSeats}>
                    {quota === 0
                      ? renderAddButton(
                        () => updateTestingProductSeats(option.feature)(1),
                        addButtonBlockedHint,
                      )
                      : (
                        <SelectQuota
                          productId={getProductId(option.feature)}
                          initQuota={0}
                          quota={quota}
                          setQuota={updateTestingProductSeats(option.feature)}
                          width={30}
                          height={32}
                        />
                      )}
                  </Box>

                  <Box className={classes.testingPriceCell}>
                    <Typography className={classes.testingPriceAmount}>
                      {formatCurrency(intl, effectivePrice, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                    </Typography>
                    <Typography className={classes.priceUnit}>
                      {intl.formatMessage(
                        {
                          id: interval === PlanInterval.YEAR
                            ? 'page.plan.v2.true.testing.price.unit.year'
                            : 'page.plan.v2.true.testing.price.unit.month',
                        },
                        { unit: option.unit },
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      )}
    </Box>
  );
};
